import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { Header } from '../../shared/layout/Header';
import { getEasternDate, getTimeUntilNextDay } from '../wordle/utils/dailyWordUtils';
import {
  calculateDistance,
  calculateScore,
  formatDistance,
  getScoreRating
} from './utils/distanceUtils';
import {
  saveGameResult,
  getComparisonStats
} from './services/gameResultsService';
import {
  getPerformanceMessage,
  getAverageComparison
} from './utils/statsUtils';
import {
  generateShareText,
  copyToClipboard
} from './utils/shareUtils';
import { getTodaysLocation } from './utils/dailyLocationUtils';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './CampusGuesser.css';

export default function CampusGuesser() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);

  // Game Data State
  const [locationId, setLocationId] = useState(null);
  const [actualLocation, setActualLocation] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // User Interaction State
  const [guessLocation, setGuessLocation] = useState(null);
  const [userMarker, setUserMarker] = useState(null);
  const [actualMarker, setActualMarker] = useState(null);

  // Game Flow State
  const [gamePhase, setGamePhase] = useState('playing'); // 'playing', 'submitted', 'completed'
  const [score, setScore] = useState(null);
  const [distance, setDistance] = useState(null);
  const [showResetViewButton, setShowResetViewButton] = useState(false);

  // Comparison Stats State
  const [comparisonStats, setComparisonStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Share State
  const [shareButtonText, setShareButtonText] = useState('Share Results');

  // UI State
  const [showResultModal, setShowResultModal] = useState(false);
  const [photoExpanded, setPhotoExpanded] = useState(false); // Mobile tap-to-expand photo

  // Loading State
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Map Instance State
  const [mapInstance, setMapInstance] = useState(null);

  // Persistence State
  const [gameStartTime, setGameStartTime] = useState(null);
  const [previousResult, setPreviousResult] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });

  // LocalStorage key generator
  const getGameStateKey = useCallback((userId) => {
    return `campus_guesser_${userId}_${getEasternDate()}`;
  }, []);

  // Save game state to localStorage
  const saveGameState = useCallback((state) => {
    if (!currentUser) return;

    try {
      const gameState = {
        guessLocation: state.guessLocation !== undefined ? state.guessLocation : guessLocation,
        gamePhase: state.gamePhase || gamePhase,
        distance: state.distance !== undefined ? state.distance : distance,
        score: state.score !== undefined ? state.score : score,
        gameStartTime: state.gameStartTime || gameStartTime,
        result: state.result || previousResult,
        timestamp: Date.now()
      };

      localStorage.setItem(getGameStateKey(currentUser.uid), JSON.stringify(gameState));
    } catch (error) {
      console.error('Failed to save game state:', error);
    }
  }, [currentUser, guessLocation, gamePhase, distance, score, gameStartTime, previousResult, getGameStateKey]);

  // Load game state from localStorage
  const loadGameState = useCallback(() => {
    if (!currentUser) return null;

    try {
      const saved = localStorage.getItem(getGameStateKey(currentUser.uid));
      if (!saved) return null;

      const gameState = JSON.parse(saved);

      // Check if saved state is from today
      const todayString = getEasternDate();
      const savedKey = getGameStateKey(currentUser.uid);
      if (!savedKey.includes(todayString)) {
        localStorage.removeItem(savedKey);
        return null;
      }

      return gameState;
    } catch (error) {
      console.error('Failed to load game state:', error);
      return null;
    }
  }, [currentUser, getGameStateKey]);

  // Clear game state from localStorage
  const clearGameState = useCallback(() => {
    if (!currentUser) return;
    try {
      localStorage.removeItem(getGameStateKey(currentUser.uid));
    } catch (error) {
      console.error('Failed to clear game state:', error);
    }
  }, [currentUser, getGameStateKey]);

  // Phase 1: Load today's location from database
  useEffect(() => {
    const loadTodaysLocation = async () => {
      try {
        const location = await getTodaysLocation();
        setLocationId(location.id);
        setPhotoUrl(location.imageUrl);
        setActualLocation({
          lat: location.actualLocation.lat,
          lng: location.actualLocation.lng
        });
        setLoading(false);
      } catch (error) {
        console.error('Failed to load location:', error);
        setLocationError(error.message || 'Failed to load today\'s location');
        setLoading(false);
      }
    };

    loadTodaysLocation();
  }, []);

  // Phase 2: Check if played today, load state
  useEffect(() => {
    const initializeGame = () => {
      try {
        if (currentUser) {
          // PRODUCTION MODE: Enable one-play-per-day restriction
          const savedState = loadGameState();

          if (savedState && savedState.result) {
            // User already completed today - show result modal by default
            const result = savedState.result;
            setPreviousResult(result);
            setShowResultModal(true);
            setGamePhase('completed');

            // Restore game state for map visualization
            setGuessLocation(result.guessLocation);
            setDistance(result.distance);
            setScore(result.score);
            // actualLocation already set from Phase 1 location loading
          } else if (savedState && savedState.guessLocation && savedState.gamePhase === 'playing') {
            // User has in-progress game (not submitted yet)
            setGuessLocation(savedState.guessLocation);
            setGamePhase(savedState.gamePhase || 'playing');
            setDistance(savedState.distance);
            setScore(savedState.score);
            setGameStartTime(savedState.gameStartTime);
          } else {
            // Start new game
            setGameStartTime(Date.now());
            setGamePhase('playing');
          }
        }
      } catch (error) {
        console.error('Error initializing game:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeGame();
  }, [currentUser, loadGameState]);

  // Phase 3: Initialize map
  useEffect(() => {
    if (!mapLoaded && !loading && locationId && mapContainerRef.current) {
      try {
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [-77.0219, 38.9213], // Howard University
          zoom: 16,
          pitch: 0,
          bearing: 0
        });

        // Disable map rotation
        map.dragRotate.disable();
        map.touchZoomRotate.disableRotation();

        map.on('load', () => {
          setMapInstance(map);
          setMapLoaded(true);
        });

        map.on('error', (e) => {
          console.error('Map error:', e);
        });
      } catch (error) {
        console.error('Failed to initialize map:', error);
      }
    }

    // Cleanup on unmount
    return () => {
      if (mapInstance) {
        mapInstance.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, locationId, mapLoaded]);

  // Add map click handler when map is ready
  useEffect(() => {
    if (!mapInstance || !mapLoaded) return;

    const handleMapClick = (e) => {
      // Only allow clicks if game is in 'playing' phase
      if (gamePhase !== 'playing') return;

      const { lng, lat } = e.lngLat;

      // Remove existing user marker if present
      if (userMarker) {
        userMarker.remove();
      }

      // Create new marker (blue for guess)
      const marker = new mapboxgl.Marker({ color: '#3B82F6' })
        .setLngLat([lng, lat])
        .addTo(mapInstance);

      setUserMarker(marker);
      setGuessLocation({ lat, lng });

      // Save to localStorage
      saveGameState({ guessLocation: { lat, lng } });
    };

    mapInstance.on('click', handleMapClick);

    return () => {
      if (mapInstance) {
        mapInstance.off('click', handleMapClick);
      }
    };
  }, [mapInstance, mapLoaded, gamePhase, userMarker, saveGameState]);

  // Restore marker if game state was loaded
  useEffect(() => {
    if (mapInstance && guessLocation && !userMarker && gamePhase === 'playing') {
      const marker = new mapboxgl.Marker({ color: '#3B82F6' })
        .setLngLat([guessLocation.lng, guessLocation.lat])
        .addTo(mapInstance);
      setUserMarker(marker);
    }
  }, [mapInstance, guessLocation, userMarker, gamePhase]);

  // Countdown timer for result modal
  useEffect(() => {
    if (showResultModal) {
      const updateCountdown = () => {
        setCountdown(getTimeUntilNextDay());
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);

      return () => clearInterval(interval);
    }
  }, [showResultModal]);

  // Reset map view to fit both markers
  const resetMapView = useCallback(() => {
    if (!mapInstance || !guessLocation || !actualLocation) return;

    const bounds = new mapboxgl.LngLatBounds()
      .extend([guessLocation.lng, guessLocation.lat])
      .extend([actualLocation.lng, actualLocation.lat]);

    mapInstance.fitBounds(bounds, {
      padding: 100,
      maxZoom: 17,
      duration: 1000
    });
  }, [mapInstance, guessLocation, actualLocation]);

  // Show result on map
  const showResultOnMap = useCallback((dist) => {
    if (!mapInstance || !guessLocation || !actualLocation) return;

    // Add popup to user's guess marker (update existing)
    if (userMarker) {
      const guessPopup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false,
        className: 'marker-popup'
      }).setHTML(`
        <div class="marker-label">
          <strong>Your Guess</strong>
        </div>
      `);
      userMarker.setPopup(guessPopup);
      guessPopup.addTo(mapInstance);
    }

    // Add actual location marker (green) with popup
    const actualPopup = new mapboxgl.Popup({
      offset: 25,
      closeButton: false,
      closeOnClick: false,
      className: 'marker-popup'
    }).setHTML(`
      <div class="marker-label">
        <strong>Actual Location</strong>
        <div class="marker-distance">${formatDistance(dist)}</div>
      </div>
    `);

    const actualMkr = new mapboxgl.Marker({ color: '#10B981' })
      .setLngLat([actualLocation.lng, actualLocation.lat])
      .setPopup(actualPopup)
      .addTo(mapInstance);

    actualPopup.addTo(mapInstance);
    setActualMarker(actualMkr);

    // Add line connecting guess to actual
    if (!mapInstance.getSource('guess-line')) {
      mapInstance.addSource('guess-line', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [guessLocation.lng, guessLocation.lat],
              [actualLocation.lng, actualLocation.lat]
            ]
          }
        }
      });

      mapInstance.addLayer({
        id: 'guess-line-layer',
        type: 'line',
        source: 'guess-line',
        paint: {
          'line-color': '#EF4444',
          'line-width': 3,
          'line-dasharray': [2, 2]
        }
      });
    }

    // Fit bounds to show both markers
    resetMapView();

    // Show reset view button
    setShowResetViewButton(true);
  }, [mapInstance, guessLocation, actualLocation, userMarker, resetMapView]);

  // Restore completed game visualization when user returns
  useEffect(() => {
    if (mapInstance && gamePhase === 'completed' && guessLocation && actualLocation && distance !== null && !actualMarker) {
      // Add user's guess marker
      const guessMkr = new mapboxgl.Marker({ color: '#3B82F6' })
        .setLngLat([guessLocation.lng, guessLocation.lat])
        .addTo(mapInstance);
      setUserMarker(guessMkr);

      // Call showResultOnMap to add actual marker and line
      showResultOnMap(distance);
      setShowResetViewButton(true);
    }
  }, [mapInstance, gamePhase, guessLocation, actualLocation, distance, actualMarker, showResultOnMap]);

  // Enable map interaction for review mode
  const enableMapInteraction = useCallback(() => {
    if (!mapInstance) return;

    mapInstance.scrollZoom.enable();
    mapInstance.boxZoom.enable();
    mapInstance.dragPan.enable();
    mapInstance.touchZoomRotate.enable();
  }, [mapInstance]);

  // Handle submit guess
  const handleSubmitGuess = useCallback(async () => {
    if (!guessLocation || !actualLocation) return;

    // Calculate distance and score
    const dist = calculateDistance(
      guessLocation.lat,
      guessLocation.lng,
      actualLocation.lat,
      actualLocation.lng
    );

    const calculatedScore = calculateScore(dist);

    setDistance(dist);
    setScore(calculatedScore);
    setGamePhase('submitted');

    // Show result on map (pass distance for labels)
    showResultOnMap(dist);
    // Keep map interactive for review mode
    enableMapInteraction();

    // Save result
    const result = {
      guessLocation,
      actualLocation,
      distance: dist,
      score: calculatedScore,
      completionTimeMs: Date.now() - gameStartTime,
      timestamp: new Date()
    };

    setPreviousResult(result);
    saveGameState({
      gamePhase: 'submitted',
      distance: dist,
      score: calculatedScore,
      result
    });

    setShowResultModal(true);

    // Log analytics (POC - just console log)
    console.log('Game completed:', {
      distance: dist,
      score: calculatedScore,
      timeSpent: Math.round((Date.now() - gameStartTime) / 1000)
    });

    // Save to Firebase and fetch comparison stats
    if (currentUser) {
      try {
        setLoadingStats(true);

        // Save result to Firestore
        await saveGameResult({
          userId: currentUser.uid,
          username: userProfile?.username || 'Anonymous',
          locationId: locationId,
          distance: dist,
          score: calculatedScore,
          guessLocation,
          actualLocation,
          completionTimeMs: Date.now() - gameStartTime
        });

        // Fetch comparison stats
        const stats = await getComparisonStats(locationId, dist);
        setComparisonStats(stats);
      } catch (error) {
        console.error('Error saving result or fetching stats:', error);
      } finally {
        setLoadingStats(false);
      }
    }
  }, [guessLocation, actualLocation, gameStartTime, currentUser, userProfile, showResultOnMap, enableMapInteraction, saveGameState]);

  // Handle photo click (mobile tap-to-expand)
  const handlePhotoClick = () => {
    // Only toggle on mobile/tablet (touch devices)
    if (window.innerWidth <= 768) {
      setPhotoExpanded(!photoExpanded);
    }
    // Desktop: hover handles expansion, click does nothing
  };

  // Handle share action - copy text to clipboard
  const handleShare = async () => {
    const shareText = generateShareText({ score, distance, stats: comparisonStats });

    const copied = await copyToClipboard(shareText);
    if (copied) {
      setShareButtonText('Copied!');
      setTimeout(() => setShareButtonText('Share Results'), 2000);
    } else {
      setShareButtonText('Failed to copy');
      setTimeout(() => setShareButtonText('Share Results'), 2000);
    }
  };

  // Handle modal dismiss (just hide modal, keep map visible)
  const handleModalDismiss = () => {
    setShowResultModal(false);
  };

  // Handle play again (reset everything)

  // Render loading screen
  const renderLoadingScreen = () => (
    <div className="game-loading">
      <div className="spinner"></div>
      <p>Loading game...</p>
    </div>
  );

  // Render error screen for no locations
  const renderErrorScreen = () => (
    <div className="game-error">
      <div className="error-icon">📸</div>
      <h2>Ran out of campus locations!</h2>
      <p className="error-message">The photographer is on vacation 🏖️</p>
      <p className="error-hint">Check back soon, or contact the admin to add more locations!</p>
      <button className="error-button" onClick={() => navigate('/')}>
        Back to Home
      </button>
    </div>
  );

  // Render result modal
  const renderResultModal = () => {
    if (!showResultModal || score === null || distance === null) return null;

    return (
      <div className="result-modal-overlay">
        <div className="result-modal" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close-button" onClick={handleModalDismiss} aria-label="Close modal">
            ✕
          </button>
          <h2>Great Guess!</h2>

          <div className="score-display-section">
            <p className="score-label">Your Score</p>
            <div className="score-display">{score}</div>
            <p className="score-rating">{getScoreRating(score)}</p>
          </div>

          {/* Comparison Stats Section */}
          {loadingStats && (
            <div className="stats-loading">
              <div className="spinner-small"></div>
              <p>Loading stats...</p>
            </div>
          )}

          {!loadingStats && (
            <div className="comparison-stats-compact">
              <div className="stats-cluster">
                <div className="cluster-item primary">
                  <span className="cluster-value">{formatDistance(distance)}</span>
                  <span className="cluster-label">Your Distance</span>
                </div>

                <div className="cluster-divider"></div>

                <div className="cluster-item">
                  <span className="cluster-value">
                    {comparisonStats && comparisonStats.totalPlayers > 0
                      ? (comparisonStats.percentile !== null
                          ? `Top ${100 - comparisonStats.percentile}%`
                          : 'First!')
                      : '-'}
                  </span>
                  <span className="cluster-label">Ranking</span>
                </div>

                <div className="cluster-divider"></div>

                <div className="cluster-item">
                  <span className="cluster-value">
                    {comparisonStats && comparisonStats.averageDistance !== null && comparisonStats.averageDistance > 0
                      ? formatDistance(comparisonStats.averageDistance)
                      : '-'}
                  </span>
                  <span className="cluster-label">Average</span>
                </div>
              </div>
            </div>
          )}

          {/* Share Section */}
          <div className="share-section">
            <button className="share-button-primary" onClick={handleShare}>
              {shareButtonText}
            </button>

            <button className="leaderboard-button" onClick={() => navigate('/leaderboard')}>
              View Leaderboard
            </button>
          </div>

          {/* Countdown to next game */}
          <div className="next-game-countdown">
            <p className="countdown-label">Next Game in:</p>
            <div className="countdown-timer">
              <div className="countdown-segment">
                <span className="countdown-number">{String(countdown.hours).padStart(2, '0')}</span>
              </div>
              <span className="countdown-separator">:</span>
              <div className="countdown-segment">
                <span className="countdown-number">{String(countdown.minutes).padStart(2, '0')}</span> 
              </div>
              <span className="countdown-separator">:</span>
              <div className="countdown-segment">
                <span className="countdown-number">{String(countdown.seconds).padStart(2, '0')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render - three conditional paths
  // Show error if location failed to load
  if (locationError) {
    return (
      <div className="campus-guesser-page">
        <Header />
        <div className="campus-guesser-container">
          {renderErrorScreen()}
        </div>
      </div>
    );
  }

  // Show loading until location is loaded
  if (loading || !locationId) {
    return (
      <div className="campus-guesser-page">
        <Header />
        <div className="campus-guesser-container">
          {renderLoadingScreen()}
        </div>
      </div>
    );
  }

  return (
    <div className="campus-guesser-page">
      <Header />
      <div className="campus-guesser-container">
        <div className="game-hero">
          <h1>Campus Guesser</h1>
          <p className="hero-subtitle">Click the map to guess where this photo was taken on Howard University campus!</p>
        </div>

        <div className="game-content">
          <div className="map-container-fullscreen">
            <div ref={mapContainerRef} id="campus-map-container"></div>

            {/* Photo thumbnail - top right */}
            <div
              className={`photo-thumbnail ${photoExpanded ? 'expanded' : ''}`}
              onClick={handlePhotoClick}
            >
              <img src={photoUrl} alt="Campus location - click to enlarge" />
            </div>

            {/* Submit Guess button - shows when user has placed a marker */}
            {guessLocation && gamePhase === 'playing' && (
              <button className="submit-guess-button" onClick={handleSubmitGuess}>
                Submit Guess
              </button>
            )}

            {/* Reset View and Review Score buttons - show after submission */}
            {showResetViewButton && (gamePhase === 'submitted' || gamePhase === 'completed') && (
              <>
                <button className="reset-view-button" onClick={resetMapView}>
                  Reset View
                </button>
                <button className="review-score-button" onClick={() => setShowResultModal(true)}>
                  Review Score
                </button>
              </>
            )}
          </div>
        </div>

        {renderResultModal()}
      </div>
    </div>
  );
}
